import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Route, 
  Plus, 
  GripVertical, 
  Trash2, 
  Edit3, 
  X,
  ChevronRight,
  BookOpen,
  Award,
  Users,
  Eye,
  EyeOff,
  Search,
  Loader2,
  Trophy,
  Target,
  Globe,
  Beaker,
  Upload,
  Image
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_BASE } from '../../config/api';
import { getThumbnailUrl } from '../../lib/utils';

const API = `${API_BASE}/api`;

// Categories aligned with CourseBuilder.jsx
const CATEGORIES = [
  'Micro-learning Unit',
  'Short Course',
  'Specialist Courses',
  'Compliance & Regulatory',
  'Leadership & Management',
  'Project Management',
  'Ethics & Governance'
];

// Specialisations aligned with CourseBuilder.jsx
const SPECIALISATIONS = [
  'Anti-Corruption',
  'Anti-Financial Crime',
  'Anti-Money Laundering',
  'Artificial Intelligence',
  'Compliance',
  'Conduct',
  'Cybersecurity',
  'Fraud',
  'GRC',
  'IT Governance',
  'Social Impact Compliance'
];

export default function LearningPathwaysAdmin({ token }) {
  const [pathways, setPathways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPathway, setEditingPathway] = useState(null);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [externalCourses, setExternalCourses] = useState([]);
  const [availableLabs, setAvailableLabs] = useState([]);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [labSearchQuery, setLabSearchQuery] = useState('');
  const [courseSourceFilter, setCourseSourceFilter] = useState('all'); // 'all', 'internal', 'external'
  const [uploadingBadge, setUploadingBadge] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    specialisation: '',
    courses: [],
    labs: [],
    estimated_duration: '',
    target_roles: [],
    is_published: false,
    badge_name: '',
    badge_image_url: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPathways();
    fetchCourses();
    fetchExternalCourses();
    fetchLabs();
  }, []);

  const fetchPathways = async () => {
    try {
      const response = await axios.get(`${API}/admin/learning-pathways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPathways(response.data);
    } catch (error) {
      console.error('Failed to fetch pathways:', error);
      toast.error('Failed to load learning pathways');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API}/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableCourses(response.data.courses || response.data || []);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    }
  };

  const fetchExternalCourses = async () => {
    try {
      const response = await axios.get(`${API}/admin/external-courses?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Mark external courses with a flag and normalize structure
      const courses = (response.data.courses || []).map(c => ({
        ...c,
        is_external: true,
        category: c.specialisation || 'External Content', // Use specialisation for display
        cpd_points: c.cpd_points || 0
      }));
      setExternalCourses(courses);
    } catch (error) {
      console.error('Failed to fetch external courses:', error);
    }
  };

  const fetchLabs = async () => {
    try {
      const response = await axios.get(`${API}/admin/labs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableLabs(response.data.labs || response.data || []);
    } catch (error) {
      console.error('Failed to fetch labs:', error);
    }
  };

  // Handle badge image upload
  const handleBadgeImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingBadge(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/upload/badge-image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        badge_image_url: response.data.url
      }));
      toast.success('Badge image uploaded');
    } catch (error) {
      console.error('Failed to upload badge image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingBadge(false);
    }
  };

  const openCreateModal = () => {
    setEditingPathway(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      specialisation: '',
      courses: [],
      labs: [],
      estimated_duration: '',
      target_roles: [],
      is_published: false,
      badge_name: '',
      badge_image_url: ''
    });
    setShowModal(true);
  };

  const openEditModal = (pathway) => {
    setEditingPathway(pathway);
    setFormData({
      title: pathway.title,
      description: pathway.description,
      category: pathway.category,
      specialisation: pathway.specialisation || '',
      courses: pathway.courses || [],
      labs: pathway.labs || [],
      estimated_duration: pathway.estimated_duration || '',
      target_roles: pathway.target_roles || [],
      is_published: pathway.is_published,
      badge_name: pathway.badge_info?.name || '',
      badge_image_url: pathway.badge_info?.image_url || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingPathway) {
        await axios.put(
          `${API}/admin/learning-pathways/${editingPathway.pathway_id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Learning pathway updated');
      } else {
        await axios.post(
          `${API}/admin/learning-pathways`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Learning pathway created');
      }
      setShowModal(false);
      fetchPathways();
    } catch (error) {
      console.error('Failed to save pathway:', error);
      toast.error('Failed to save learning pathway');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pathwayId) => {
    if (!window.confirm('Are you sure you want to delete this learning pathway?')) return;
    
    try {
      await axios.delete(`${API}/admin/learning-pathways/${pathwayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Learning pathway deleted');
      fetchPathways();
    } catch (error) {
      toast.error('Failed to delete pathway');
    }
  };

  const togglePublish = async (pathway) => {
    try {
      await axios.put(
        `${API}/admin/learning-pathways/${pathway.pathway_id}`,
        { is_published: !pathway.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(pathway.is_published ? 'Pathway unpublished' : 'Pathway published');
      fetchPathways();
    } catch (error) {
      toast.error('Failed to update pathway');
    }
  };

  const addCourseToPathway = (courseId) => {
    if (formData.courses.includes(courseId)) {
      toast.info('Course already in pathway');
      return;
    }
    setFormData(prev => ({
      ...prev,
      courses: [...prev.courses, courseId]
    }));
  };

  const removeCourseFromPathway = (courseId) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.filter(id => id !== courseId)
    }));
  };

  const moveCourse = (fromIndex, toIndex) => {
    const newCourses = [...formData.courses];
    const [removed] = newCourses.splice(fromIndex, 1);
    newCourses.splice(toIndex, 0, removed);
    setFormData(prev => ({ ...prev, courses: newCourses }));
  };

  const getCourseById = (courseId) => {
    // Check internal courses first, then external
    return availableCourses.find(c => c.course_id === courseId) ||
           externalCourses.find(c => c.course_id === courseId);
  };

  // Combine internal and external courses based on filter
  const allCourses = courseSourceFilter === 'internal' 
    ? availableCourses 
    : courseSourceFilter === 'external'
      ? externalCourses
      : [...availableCourses, ...externalCourses];

  const filteredCourses = allCourses.filter(course => 
    !formData.courses.includes(course.course_id) &&
    (course.title?.toLowerCase().includes(courseSearchQuery.toLowerCase()) ||
     course.category?.toLowerCase().includes(courseSearchQuery.toLowerCase()) ||
     course.specialisation?.toLowerCase().includes(courseSearchQuery.toLowerCase()))
  );

  const totalCPD = formData.courses.reduce((sum, courseId) => {
    const course = getCourseById(courseId);
    return sum + (course?.cpd_points || 0);
  }, 0);

  // Lab helper functions
  const addLabToPathway = (labId) => {
    if (formData.labs.includes(labId)) {
      toast.info('Lab already in pathway');
      return;
    }
    setFormData(prev => ({
      ...prev,
      labs: [...prev.labs, labId]
    }));
  };

  const removeLabFromPathway = (labId) => {
    setFormData(prev => ({
      ...prev,
      labs: prev.labs.filter(id => id !== labId)
    }));
  };

  const getLabById = (labId) => {
    return availableLabs.find(l => l.lab_id === labId);
  };

  const filteredLabs = availableLabs.filter(lab => 
    !formData.labs.includes(lab.lab_id) &&
    (lab.title?.toLowerCase().includes(labSearchQuery.toLowerCase()) ||
     lab.topic?.toLowerCase().includes(labSearchQuery.toLowerCase()) ||
     lab.industry?.toLowerCase().includes(labSearchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-navigate-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Route className="h-7 w-7 text-navigate-gold" />
            Learning Pathways
          </h2>
          <p className="text-zinc-400 mt-1">Create structured learning journeys by linking courses</p>
        </div>
        <Button onClick={openCreateModal} className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">
          <Plus className="h-4 w-4 mr-2" />
          Create Pathway
        </Button>
      </div>

      {/* Pathways List */}
      {pathways.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/50 rounded-xl border border-zinc-700">
          <Route className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">No learning pathways created yet</p>
          <Button onClick={openCreateModal} variant="outline" className="border-zinc-600">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Pathway
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {pathways.map(pathway => (
            <div 
              key={pathway.pathway_id}
              className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{pathway.title}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      pathway.is_published 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-zinc-600/50 text-zinc-400'
                    }`}>
                      {pathway.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-sm mb-4 line-clamp-2">{pathway.description}</p>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm flex-wrap">
                    <span className="flex items-center gap-2 text-zinc-400">
                      <BookOpen className="h-4 w-4" />
                      {pathway.courses?.length || 0} Courses
                    </span>
                    {(pathway.labs?.length || 0) > 0 && (
                      <span className="flex items-center gap-2 text-purple-400">
                        <Beaker className="h-4 w-4" />
                        {pathway.labs.length} Labs
                      </span>
                    )}
                    <span className="flex items-center gap-2 text-navigate-gold">
                      <Award className="h-4 w-4" />
                      {pathway.cpd_points_total || 0} CPD Points
                    </span>
                    <span className="flex items-center gap-2 text-zinc-400">
                      <Users className="h-4 w-4" />
                      {pathway.category}
                    </span>
                    {/* Badge Preview - Admin uploaded */}
                    {pathway.badge_info?.image_url && (
                      <span className="flex items-center gap-2 text-[#C8A85A]">
                        <img 
                          src={pathway.badge_info.image_url} 
                          alt={pathway.badge_info.name}
                          className="w-5 h-5 rounded object-cover"
                        />
                        {pathway.badge_info.name || 'Badge'}
                      </span>
                    )}
                  </div>
                  
                  {/* Course Preview */}
                  {pathway.course_details?.length > 0 && (
                    <div className="flex items-center gap-2 mt-4">
                      {pathway.course_details.slice(0, 4).map((course, idx) => (
                        <div key={course.course_id} className="flex items-center">
                          <div 
                            className="w-10 h-10 rounded-lg bg-zinc-700 overflow-hidden border-2 border-zinc-600"
                            title={course.title}
                          >
                            {course.thumbnail_url ? (
                              <img src={getThumbnailUrl(course.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xs text-zinc-400">
                                {idx + 1}
                              </div>
                            )}
                          </div>
                          {idx < Math.min(pathway.course_details.length - 1, 3) && (
                            <ChevronRight className="h-4 w-4 text-zinc-600 mx-1" />
                          )}
                        </div>
                      ))}
                      {pathway.course_details.length > 4 && (
                        <span className="text-xs text-zinc-500 ml-2">
                          +{pathway.course_details.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => togglePublish(pathway)}
                    className={`p-2 rounded-lg transition-colors ${
                      pathway.is_published 
                        ? 'text-green-400 hover:bg-green-500/10' 
                        : 'text-zinc-400 hover:bg-zinc-700'
                    }`}
                    title={pathway.is_published ? 'Unpublish' : 'Publish'}
                  >
                    {pathway.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => openEditModal(pathway)}
                    className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pathway.pathway_id)}
                    className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between shrink-0">
              <h2 className="text-xl font-bold text-white">
                {editingPathway ? 'Edit Learning Pathway' : 'Create Learning Pathway'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-zinc-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Left Column - Details */}
                <div className="space-y-4">
                  <div>
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="bg-zinc-900 border-zinc-700"
                      placeholder="e.g., Compliance Fundamentals Track"
                    />
                  </div>

                  <div>
                    <Label>Description *</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="bg-zinc-900 border-zinc-700 min-h-[100px]"
                      placeholder="Describe what learners will achieve..."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category *</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(v) => setFormData({...formData, category: v})}
                      >
                        <SelectTrigger className="bg-zinc-900 border-zinc-700">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Specialisation</Label>
                      <Select 
                        value={formData.specialisation} 
                        onValueChange={(v) => setFormData({...formData, specialisation: v})}
                      >
                        <SelectTrigger className="bg-zinc-900 border-zinc-700">
                          <SelectValue placeholder="Select specialisation" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-700">
                          {SPECIALISATIONS.map(spec => (
                            <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Est. Duration</Label>
                      <Input
                        value={formData.estimated_duration}
                        onChange={(e) => setFormData({...formData, estimated_duration: e.target.value})}
                        className="bg-zinc-900 border-zinc-700"
                        placeholder="e.g., 10 hours"
                      />
                    </div>
                  </div>

                  {/* Pathway Completion Badge - Admin Upload */}
                  <div>
                    <Label className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-[#C8A85A]" />
                      Pathway Completion Badge (Optional)
                    </Label>
                    <p className="text-xs text-zinc-500 mb-3">
                      Upload a badge image that learners earn when they complete this pathway
                    </p>
                    
                    {/* Badge Name Input */}
                    <div className="mb-3">
                      <Label className="text-xs text-zinc-400">Badge Name</Label>
                      <Input
                        value={formData.badge_name}
                        onChange={(e) => setFormData({...formData, badge_name: e.target.value})}
                        className="bg-zinc-900 border-zinc-700 mt-1"
                        placeholder="e.g., Compliance Specialist"
                      />
                    </div>
                    
                    {/* Badge Image Upload */}
                    <div className="flex items-start gap-4">
                      {/* Preview */}
                      <div className="shrink-0">
                        {formData.badge_image_url ? (
                          <div className="relative">
                            <img 
                              src={formData.badge_image_url} 
                              alt="Badge preview"
                              className="w-20 h-20 rounded-xl object-cover border-2 border-[#C8A85A]/50"
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({...formData, badge_image_url: ''})}
                              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="w-20 h-20 rounded-xl bg-zinc-800 border-2 border-dashed border-zinc-600 flex items-center justify-center">
                            <Image className="h-8 w-8 text-zinc-600" />
                          </div>
                        )}
                      </div>
                      
                      {/* Upload Button */}
                      <div className="flex-1">
                        <label className="block">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleBadgeImageUpload}
                            className="hidden"
                            disabled={uploadingBadge}
                          />
                          <div className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 rounded-lg cursor-pointer transition-colors">
                            {uploadingBadge ? (
                              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                            ) : (
                              <Upload className="h-4 w-4 text-zinc-400" />
                            )}
                            <span className="text-sm text-zinc-300">
                              {uploadingBadge ? 'Uploading...' : 'Upload Badge Image'}
                            </span>
                          </div>
                        </label>
                        <p className="text-xs text-zinc-500 mt-2">
                          Recommended: 200x200px, PNG or JPG, max 5MB
                        </p>
                      </div>
                    </div>
                    
                    {/* Badge Preview Card */}
                    {formData.badge_image_url && formData.badge_name && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-[#C8A85A]/10 to-zinc-900 border border-[#C8A85A]/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <img 
                            src={formData.badge_image_url} 
                            alt={formData.badge_name}
                            className="w-12 h-12 rounded-lg object-cover border border-[#C8A85A]/50"
                          />
                          <div>
                            <p className="text-sm font-medium text-white">{formData.badge_name}</p>
                            <p className="text-xs text-zinc-400">Awarded on pathway completion</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Stats Preview */}
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">Total Courses:</span>
                      <span className="text-white font-medium">{formData.courses.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-zinc-400">Learning Labs:</span>
                      <span className="text-purple-400 font-medium">{formData.labs.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-zinc-400">Total CPD Points:</span>
                      <span className="text-navigate-gold font-medium">{totalCPD}</span>
                    </div>
                  </div>
                </div>

                {/* Right Column - Course Selection */}
                <div className="space-y-4">
                  <div>
                    <Label>Courses in Pathway ({formData.courses.length})</Label>
                    <div className="mt-2 bg-zinc-900 border border-zinc-700 rounded-lg min-h-[200px] max-h-[300px] overflow-y-auto">
                      {formData.courses.length === 0 ? (
                        <div className="p-4 text-center text-zinc-500 text-sm">
                          Add courses from the list below
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {formData.courses.map((courseId, idx) => {
                            const course = getCourseById(courseId);
                            const isExternal = course?.is_external || courseId.startsWith('ext_');
                            return (
                              <div 
                                key={courseId}
                                className={`flex items-center gap-2 p-2 rounded-lg ${
                                  isExternal ? 'bg-purple-900/30 border border-purple-500/30' : 'bg-zinc-800'
                                }`}
                              >
                                <GripVertical className="h-4 w-4 text-zinc-600 cursor-move" />
                                <span className={`w-6 h-6 ${isExternal ? 'bg-purple-500' : 'bg-navigate-gold'} text-black rounded-full flex items-center justify-center text-xs font-bold`}>
                                  {idx + 1}
                                </span>
                                {isExternal && <Globe className="h-3 w-3 text-purple-400 shrink-0" />}
                                <span className="flex-1 text-sm text-white truncate">
                                  {course?.title || courseId}
                                </span>
                                <span className={`text-xs ${isExternal ? 'text-purple-400' : 'text-navigate-gold'}`}>
                                  {course?.cpd_points || 0} CPD
                                </span>
                                <button
                                  onClick={() => removeCourseFromPathway(courseId)}
                                  className="p-1 text-zinc-400 hover:text-red-400"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Add Courses</Label>
                    {/* Course Source Filter */}
                    <div className="flex gap-2 mt-2 mb-2">
                      <button
                        onClick={() => setCourseSourceFilter('all')}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          courseSourceFilter === 'all' 
                            ? 'bg-navigate-gold text-black' 
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        All ({availableCourses.length + externalCourses.length})
                      </button>
                      <button
                        onClick={() => setCourseSourceFilter('internal')}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          courseSourceFilter === 'internal' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        <BookOpen className="h-3 w-3 inline mr-1" />
                        Internal ({availableCourses.length})
                      </button>
                      <button
                        onClick={() => setCourseSourceFilter('external')}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${
                          courseSourceFilter === 'external' 
                            ? 'bg-purple-500 text-white' 
                            : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}
                      >
                        <Globe className="h-3 w-3 inline mr-1" />
                        External ({externalCourses.length})
                      </button>
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                      <Input
                        value={courseSearchQuery}
                        onChange={(e) => setCourseSearchQuery(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 pl-10"
                        placeholder="Search courses..."
                      />
                    </div>
                    <div className="mt-2 bg-zinc-900 border border-zinc-700 rounded-lg max-h-[200px] overflow-y-auto">
                      {filteredCourses.length === 0 ? (
                        <div className="p-4 text-center text-zinc-500 text-sm">
                          No courses found
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {filteredCourses.slice(0, 20).map(course => (
                            <button
                              key={course.course_id}
                              onClick={() => addCourseToPathway(course.course_id)}
                              className="w-full flex items-center gap-3 p-2 hover:bg-zinc-800 rounded-lg text-left transition-colors"
                            >
                              <Plus className="h-4 w-4 text-green-400" />
                              {course.is_external && (
                                <Globe className="h-3 w-3 text-purple-400 shrink-0" />
                              )}
                              <span className="flex-1 text-sm text-white truncate">{course.title}</span>
                              <span className={`text-xs ${course.is_external ? 'text-purple-400' : 'text-zinc-400'}`}>
                                {course.is_external ? course.specialisation : course.category}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Learning Labs Section */}
                  <div className="border-t border-zinc-700 pt-4 mt-4">
                    <Label className="flex items-center gap-2">
                      <Beaker className="h-4 w-4 text-purple-400" />
                      Learning Labs in Pathway ({formData.labs.length})
                    </Label>
                    <div className="mt-2 bg-zinc-900 border border-zinc-700 rounded-lg min-h-[100px] max-h-[200px] overflow-y-auto">
                      {formData.labs.length === 0 ? (
                        <div className="p-4 text-center text-zinc-500 text-sm">
                          Add learning labs from below (optional)
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {formData.labs.map((labId, idx) => {
                            const lab = getLabById(labId);
                            return (
                              <div 
                                key={labId}
                                className="flex items-center gap-2 p-2 bg-purple-500/10 border border-purple-500/30 rounded-lg"
                              >
                                <Beaker className="h-4 w-4 text-purple-400" />
                                <span className="flex-1 text-sm text-white truncate">
                                  {lab?.title || labId}
                                </span>
                                <span className="text-xs text-purple-400">
                                  {lab?.level || 'Lab'}
                                </span>
                                <button
                                  onClick={() => removeLabFromPathway(labId)}
                                  className="p-1 text-zinc-400 hover:text-red-400"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Add Learning Labs</Label>
                    <div className="relative mt-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                      <Input
                        value={labSearchQuery}
                        onChange={(e) => setLabSearchQuery(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 pl-10"
                        placeholder="Search labs..."
                      />
                    </div>
                    <div className="mt-2 bg-zinc-900 border border-zinc-700 rounded-lg max-h-[150px] overflow-y-auto">
                      {filteredLabs.length === 0 ? (
                        <div className="p-4 text-center text-zinc-500 text-sm">
                          No learning labs found
                        </div>
                      ) : (
                        <div className="p-2 space-y-1">
                          {filteredLabs.slice(0, 15).map(lab => (
                            <button
                              key={lab.lab_id}
                              onClick={() => addLabToPathway(lab.lab_id)}
                              className="w-full flex items-center gap-3 p-2 hover:bg-purple-500/10 rounded-lg text-left transition-colors"
                            >
                              <Plus className="h-4 w-4 text-purple-400" />
                              <span className="flex-1 text-sm text-white truncate">{lab.title}</span>
                              <span className="text-xs text-zinc-400">{lab.topic || lab.industry}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-zinc-800 flex items-center justify-between shrink-0">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                  className="rounded border-zinc-600 bg-zinc-800 text-navigate-gold focus:ring-navigate-gold"
                />
                <span className="text-zinc-300">Publish immediately</span>
              </label>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowModal(false)} className="border-zinc-600">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-navigate-gold hover:bg-navigate-gold/90 text-black"
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingPathway ? 'Update Pathway' : 'Create Pathway'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
